import {
    DescriptionFieldProps,
    IconButtonProps,
    ObjectFieldTemplateProps,
    TitleFieldProps,
    FieldTemplateProps,
    ArrayFieldTemplateProps,
} from '@rjsf/utils';
import React, { CSSProperties } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    ButtonForRJSF,
    CustomFieldTemplateContainer,
    DescriptionContainer,
    DescriptionText,
    ErrorContainer,
    ObjectPropertyWrapper,
} from 'src/domains/layouts/webview/ConfigurationManager/components/customTemplates/customTemplates.style';
import {
    ObjectFieldTemplate,
    TitleField,
} from 'src/domains/layouts/webview/ConfigurationManager/components/customComponents.style';

export const DescriptionFieldTemplate = observer(
    'DescriptionFieldTemplate',
    (props: DescriptionFieldProps): JSX.Element | null => {
        const { description, id } = props;
        if (typeof description === 'string' && description.trim().length === 0) {
            return null;
        }
        return (
            <DescriptionContainer id={id}>
                <DescriptionText>{description}</DescriptionText>
            </DescriptionContainer>
        );
    }
);

export const CustomFieldTemplate = observer(
    'CustomFieldTemplate',
    ({ id, label, required, children, rawDescription }: FieldTemplateProps): JSX.Element => {
        // console.log('CustomFieldTemplate ', rest, id, label);
        const desc = rawDescription ?? label;
        return (
            <CustomFieldTemplateContainer>
                {id === 'root' ? null : (
                    <div>
                        <label htmlFor={id}>
                            <DescriptionText>
                                {desc}
                                {required === true && desc.trim() !== '' && '*'}
                            </DescriptionText>
                        </label>
                    </div>
                )}
                {children}
            </CustomFieldTemplateContainer>
        );
    }
);

export const TitleFieldTemplate = observer('TitleFieldTemplate', (props: TitleFieldProps): JSX.Element => {
    // console.log('TitleFieldTemplate ', props);
    const { id, required, title } = props;
    return (
        <TitleField id={id}>
            {title}
            {required === true && <span>*</span>}
        </TitleField>
    );
});

export const CustomObjectFieldTemplate = observer(
    'CustomObjectFieldTemplate',
    (props: ObjectFieldTemplateProps): JSX.Element => {
        // console.log('CustomObjectFieldTemplate', props);
        const isRootWrapper = props.idSchema.$id === 'root';
        const withChild = props.properties.length > 1;
        return (
            <ObjectFieldTemplate
                style={
                    withChild && isRootWrapper === false ? { borderLeft: '2px solid #e0e0e0', paddingLeft: '8px' } : {}
                }
            >
                {/*{props.title !== '' && <LegendFieldTemplate>{props.title}</LegendFieldTemplate>}*/}
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {/*{props.description !== null && <p>{props.description}</p>}*/}

                {props.properties.map((element, index) => (
                    <ObjectPropertyWrapper
                        key={index}
                        className='property-wrapper'
                    >
                        {/* render custom element -> content is a react element */}
                        {element.content}
                    </ObjectPropertyWrapper>
                ))}
            </ObjectFieldTemplate>
        );
    }
);

export function CustomArrayFieldTemplate(props: ArrayFieldTemplateProps): JSX.Element {
    console.log('props ', props);
    return (
        <div>
            {props.items.map((element) => {
                const {
                    children,
                    className,
                    disabled,
                    hasToolbar,
                    hasMoveDown,
                    hasMoveUp,
                    hasRemove,
                    hasCopy,
                    index,
                    onCopyIndexClick,
                    onDropIndexClick,
                    onReorderClick,
                    readonly,
                    registry,
                    uiSchema,
                    key,
                } = element;
                console.log('element ', element);
                const { CopyButton, MoveDownButton, MoveUpButton, RemoveButton } = registry.templates.ButtonTemplates;
                const btnStyle: CSSProperties = {
                    fontWeight: 'bold',
                    marginRight: '4px',
                    height: '22px',
                    fontSize: '10px',
                };
                return (
                    <div
                        className={className}
                        key={key}
                    >
                        <div className={hasToolbar ? 'col-xs-9' : 'col-xs-12'}>{children}</div>
                        {hasToolbar && (
                            <div className='col-xs-3 array-item-toolbox'>
                                <div
                                    className='btn-group'
                                    style={{
                                        display: 'flex',
                                    }}
                                >
                                    {(hasMoveUp || hasMoveDown) && (
                                        <MoveUpButton
                                            disabled={disabled === true || readonly === true || !hasMoveUp}
                                            style={btnStyle}
                                            onClick={onReorderClick(index, index - 1)}
                                            uiSchema={uiSchema}
                                            registry={registry}
                                        />
                                    )}
                                    {(hasMoveUp || hasMoveDown) && (
                                        <MoveDownButton
                                            style={btnStyle}
                                            disabled={disabled === true || readonly === true || !hasMoveDown}
                                            onClick={onReorderClick(index, index + 1)}
                                            uiSchema={uiSchema}
                                            registry={registry}
                                        />
                                    )}
                                    {hasCopy && (
                                        <CopyButton
                                            style={btnStyle}
                                            disabled={disabled === true || readonly}
                                            onClick={onCopyIndexClick(index)}
                                            uiSchema={uiSchema}
                                            registry={registry}
                                        />
                                    )}
                                    {hasRemove && (
                                        <RemoveButton
                                            style={btnStyle}
                                            disabled={disabled === true || readonly}
                                            onClick={onDropIndexClick(index)}
                                            uiSchema={uiSchema}
                                            registry={registry}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
            {props.canAdd === true && (
                <div style={{ marginTop: '10px' }}>
                    <AddButton
                        onClick={props.onAddClick}
                        registry={props.registry}
                        uiSchema={props.uiSchema}
                    />
                </div>
            )}
        </div>
    );
}

export const CustomErrorList = observer(
    'CustomErrorList',
    (props: { errors: Array<{ stack: string }> }): JSX.Element => {
        console.log('CustomErrorList props ', props);
        const { errors } = props;
        return (
            <ErrorContainer>
                <h2>Form submit errors: </h2>
                <ul>
                    {errors.map((error: { stack: string }) => (
                        <li key={error.stack}>{error.stack}</li>
                    ))}
                </ul>
            </ErrorContainer>
        );
    }
);

export const CustomButton = (
    props: IconButtonProps,
    type: 'Move down' | 'Move up' | 'Remove' | 'Add new',
    variant?: 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link' | 'loading'
): JSX.Element => {
    const { icon, iconType, ...btnProps } = props;
    return (
        <ButtonForRJSF
            {...btnProps}
            variant={variant ?? 'primary'}
        >
            {type}
        </ButtonForRJSF>
    );
};

export const AddButton = (props: IconButtonProps): JSX.Element => {
    return CustomButton(props, 'Add new', 'primary');
};

export const MoveDownButton = (props: IconButtonProps): JSX.Element => {
    return CustomButton(props, 'Move down', 'secondary');
};

export const MoveUpButton = (props: IconButtonProps): JSX.Element => {
    return CustomButton(props, 'Move up', 'secondary');
};

export const RemoveButton = (props: IconButtonProps): JSX.Element => {
    return CustomButton(props, 'Remove', 'destructive');
};

export const SubmitButton = (props: IconButtonProps): JSX.Element => {
    const { icon, iconType, ...btnProps } = props;
    return (
        <div style={{ marginTop: '10px' }}>
            <ButtonForRJSF
                {...btnProps}
                variant='primary'
            >
                Submit
            </ButtonForRJSF>
        </div>
    );
};
